import { UserIcon } from '@heroicons/react/16/solid';
import { BeakerIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { FF_SAMPLING_EXAMPLE_ITEMS } from 'src/settings';
import { EmptyTabState } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { useFeatureFlag } from 'src/shared/hooks';
import { ExampleItem } from 'src/shared/types/exampleItem';
import { ProductById } from 'src/shared/types/product';

import ClearcoBanner from 'src/shared/components/ClearcoBanner';
import { Spinner } from 'src/shared/components/ui';
import { Tab, TabsV2, Typography } from 'src/shared/components/ui';

import useProductSamplingExampleItems from '../../hooks/useProductSamplingExampleItems';
import useProductSamplings from '../../hooks/useProductSamplings';
import getSamplingSupplierCloaks from '../../utils/getSamplingSupplierCloaks';
import SamplingExampleItemsTable from '../sampling-example-items-table/SamplingExampleItemsTable';
import SamplingsTable from '../sampling-table/SamplingsTable';

interface ProductDetailSamplingsTabProps {
  product: ProductById;
}

const ProductDetailSamplingsTab: React.FC<ProductDetailSamplingsTabProps> = ({
  product
}) => {
  const { data: samplings, isSuccess } = useProductSamplings(product.id);
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const {
    isFeatureEnabled: isSamplingExampleItemsEnabled,
    isLoading: isExampleItemsFlagLoading
  } = useFeatureFlag(FF_SAMPLING_EXAMPLE_ITEMS);

  const { data: exampleItems, isLoading: isExampleItemsLoading } =
    useProductSamplingExampleItems(
      isSamplingExampleItemsEnabled ? product.id : ''
    );
  const nonArchivedExampleItems = useMemo(() => {
    return exampleItems?.filter(
      (exampleItem: ExampleItem) => !exampleItem.archived
    );
  }, [exampleItems]);

  const nonArchivedSamplings = useMemo(() => {
    return samplings?.filter((s) => !s.archived);
  }, [samplings]);

  const supplierCloaks = useMemo(
    () => getSamplingSupplierCloaks(samplings),
    [samplings]
  );

  // make this into a resuable component
  const renderSupplierCloaks = () => {
    return supplierCloaks.map((cloak) => (
      <Tab
        key={cloak}
        title={
          <div className="flex items-center gap-1">
            <UserIcon className="size-4" />
            <span>{cloak}</span>
          </div>
        }
      />
    ));
  };

  // Wait for the flag and the example items too: the empty state depends on both,
  // otherwise "No samples" flashes before the References table appears.
  if (!isSuccess || isExampleItemsFlagLoading || isExampleItemsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  const hasNoSamplings = !nonArchivedSamplings?.length;
  const hasNoExampleItems =
    !isSamplingExampleItemsEnabled || !nonArchivedExampleItems?.length;

  if (hasNoSamplings && hasNoExampleItems) {
    return (
      <EmptyTabState
        icon={BeakerIcon}
        title="No samples"
        description="Your samples will appear here after being ordered"
        action={
          <Link
            to={`${ROUTE_PATHS.PRODUCTS}/${product.id}?tab=quotes`}
            className="text-primary underline text-sm font-medium"
          >
            Go to quotes
          </Link>
        }
      />
    );
  }

  return (
    <div>
      <div>
        <Typography
          className="flex items-center gap-2 h-[33px]"
          variant="h4"
          as="h2"
          weight="bold"
        >
          {nonArchivedSamplings?.length}{' '}
          {nonArchivedSamplings?.length === 1 ? 'Sample' : 'Samples'}
        </Typography>

        <div className="mt-3">
          <TabsV2
            selectedKey={selectedSupplier}
            onSelectionChange={(key) => setSelectedSupplier(key as string)}
            color="dark"
            variant="light"
            classNames={{ tabList: '!px-0' }}
          >
            <Tab key="all" title="All" />
            {renderSupplierCloaks()}
          </TabsV2>
        </div>

        <SamplingsTable
          key={`samples-table-${product?.id}`}
          samplings={samplings}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          selectedSupplier={selectedSupplier}
        />
      </div>

      {isSamplingExampleItemsEnabled && (
        <div className="mt-6">
          <Typography
            className="flex items-center gap-2 h-[33px]"
            variant="h4"
            as="h2"
            weight="bold"
          >
            {nonArchivedExampleItems?.length}{' '}
            {nonArchivedExampleItems?.length === 1 ? 'Reference' : 'References'}
          </Typography>

          <SamplingExampleItemsTable
            key={`samples-example-items-table-${product?.id}`}
            exampleItems={exampleItems || []}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            selectedSupplier={selectedSupplier}
          />
        </div>
      )}

      {!isLoading && <ClearcoBanner />}
    </div>
  );
};

export default ProductDetailSamplingsTab;
